<template>
    <div class="line-chart-container">
        <canvas ref="chart"></canvas>
    </div>
</template>

<script>
import Chart from 'chart.js/auto'

export default {
    name: 'LineChart',
    props: {
        labels: {
            type: Array,
            required: true,
        },
        datasets: {
            type: Array,
            required: true,
            // Expected format: [{ label: 'Dataset 1', data: [...], color: '#4A3AFF' }]
        },
        showPoints: {
            type: Boolean,
            default: true,
        },
        tooltipPrefix: {
            type: String,
            default: '',
        },
        tooltipSuffix: {
            type: String,
            default: () => window.symbolCurrency || '€',
        },
    },
    data() {
        return {
            chart: null,
        }
    },
    watch: {
        datasets: {
            deep: true,
            handler() {
                this.updateChart()
            },
        },
        labels() {
            this.updateChart()
        },
    },
    mounted() {
        this.createChart()
    },
    beforeDestroy() {
        if (this.chart) {
            this.chart.destroy()
        }
    },
    methods: {
        createChart() {
            const ctx = this.$refs.chart.getContext('2d')

            this.chart = new Chart(ctx, {
                type: 'line',
                data: {
                    labels: this.labels,
                    datasets: this.buildDatasets(),
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                        mode: 'index',
                        intersect: false,
                    },
                    plugins: {
                        legend: {
                            display: true,
                            position: 'bottom',
                            labels: {
                                color: '#FFF',
                                usePointStyle: true,
                                pointStyle: 'circle',
                                padding: 20,
                                font: {
                                    family: 'Poppins',
                                    size: 12,
                                },
                            },
                        },
                        tooltip: {
                            backgroundColor: '#D9D9D9',
                            titleColor: '#1E1B39',
                            bodyColor: '#1E1B39',
                            padding: 10,
                            cornerRadius: 8,
                            displayColors: false,
                            callbacks: {
                                label: (context) => {
                                    return `${context.dataset.label}: ${this.tooltipPrefix}${context.parsed.y.toLocaleString(
                                        'fr-FR',
                                        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                                    )}${this.tooltipSuffix}`
                                },
                            },
                        },
                    },
                    scales: {
                        y: {
                            beginAtZero: true,
                            grid: {
                                color: 'rgba(229, 229, 239, 0.30)',
                                drawBorder: false,
                            },
                            ticks: {
                                color: '#FFF',
                                font: {
                                    family: 'Inter',
                                    size: 14,
                                },
                                callback: (value) => {
                                    if (value >= 1000) {
                                        return `${Math.round(value / 1000)}K${this.$symbolCurrency}`
                                    }
                                    return `${Math.round(value)}${this.$symbolCurrency}`
                                },
                            },
                        },
                        x: {
                            grid: {
                                display: false,
                            },
                            ticks: {
                                color: '#FFF',
                                font: {
                                    family: 'Inter',
                                    size: 14,
                                },
                            },
                        },
                    },
                },
            })
        },
        buildDatasets() {
            return this.datasets.map((dataset) => {
                return {
                    label: dataset.label,
                    data: dataset.data,
                    borderColor: dataset.color,
                    backgroundColor: 'transparent',
                    borderWidth: 2,
                    tension: 0.4, // Smooth curves
                    fill: false,
                    pointBackgroundColor: '#FFF',
                    pointBorderColor: dataset.color,
                    pointBorderWidth: 2,
                    pointRadius: this.showPoints ? 7 : 0,
                    pointHoverRadius: this.showPoints ? 9 : 4,
                }
            })
        },
        updateChart() {
            if (this.chart) {
                this.chart.data.labels = this.labels
                this.chart.data.datasets = this.buildDatasets()
                this.chart.update()
            }
        },
    },
}
</script>

<style scoped>
.line-chart-container {
    width: 100%;
    height: 300px;
    position: relative;
}
</style>
